import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";

/**
 * Mobile-friendly photo upload with client-side compression.
 * - Tap to open camera/photo library (uses capture="environment" on mobile)
 * - Resizes to max 1600px and emits base64 JPEG ~75% quality
 * - Shows a thumbnail preview with a remove button
 */
export default function PhotoUpload({ value, onChange, label, hint, testId, capture = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const compress = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1600;
          let w = img.width;
          let h = img.height;
          if (w > MAX || h > MAX) {
            const r = Math.min(MAX / w, MAX / h);
            w = Math.round(w * r);
            h = Math.round(h * r);
          }
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.75));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const handle = async (e) => {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Please pick an image file.");
      return;
    }
    setBusy(true);
    try {
      const out = await compress(file);
      onChange?.(out);
    } catch {
      setErr("Could not read that photo.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId ? `${testId}-wrapper` : undefined}>
      {label && <div className="text-sm font-medium mb-1.5 text-gray-700">{label}</div>}
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="" className="rounded-lg border border-gray-200 max-h-40 w-auto" />
          <button
            type="button"
            onClick={() => onChange?.(null)}
            data-testid={testId ? `${testId}-remove` : undefined}
            className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-7 h-7 grid place-items-center text-gray-700 hover:bg-gray-50 shadow-sm"
            aria-label="Remove photo"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <label
          className="flex flex-col items-center justify-center w-full min-h-[120px] border border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition px-4 py-5 text-center"
          data-testid={testId}
        >
          {busy ? (
            <>
              <Loader2 className="text-gray-400 animate-spin mb-1.5" size={20} />
              <span className="text-xs text-gray-500">Processing…</span>
            </>
          ) : (
            <>
              <UploadCloud className="text-gray-400 mb-1.5" size={22} />
              <span className="text-sm font-medium text-gray-700">Tap to upload</span>
              {hint && <span className="text-[11px] text-gray-500 mt-0.5">{hint}</span>}
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            {...(capture ? { capture: "environment" } : {})}
            onChange={handle}
            className="hidden"
          />
        </label>
      )}
      {err && <p className="text-xs text-red-600 mt-1.5">{err}</p>}
    </div>
  );
}
